import { useLayoutEffect, useRef } from 'react'

import { clamp } from '@/lib/scroll'

/**
 * Calls `onFrame(p)` with the scroll progress, 0 → 1, of the element in
 * `ref`, as `measure(el)` works it out from where the element sits in the
 * viewport: once on mount, then once per painted frame while it changes
 * (scroll and resize), however fast the events come.
 *
 * With `lag` (in ms) the progress glides toward the scroll position through
 * two chained smoothing stages, so motion eases in as well as out: a finger
 * flick, a mouse-wheel notch or an uneven trackpad stroke plays out as one
 * soft movement. Visitors who ask for reduced motion get the direct value.
 */
export function useScrollDriver(ref, measure, onFrame, lag = 0) {
  const latest = useRef(onFrame)
  useLayoutEffect(() => {
    latest.current = onFrame
  })

  useLayoutEffect(() => {
    const glide =
      lag > 0 && !window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
    let frame = 0
    let near = -1 // first stage: chases the scroll position
    let shown = -1 // second stage: chases the first; what's on screen
    let last = 0
    const update = (now) => {
      frame = 0
      const target = clamp(measure(ref.current))
      if (!glide || shown < 0) {
        near = shown = target
      } else {
        // Capped, so the first frame after a pause starts the glide gently.
        const k = 1 - Math.exp(-Math.min(now - last, 34) / lag)
        near += (target - near) * k
        shown += (near - shown) * k
        // Well under a pixel of scroll away: arrive.
        if (Math.abs(target - near) + Math.abs(near - shown) < 0.0002) {
          near = shown = target
        }
      }
      last = now
      latest.current(shown)
      if (shown !== target) frame = requestAnimationFrame(update)
    }
    const schedule = () => {
      frame ||= requestAnimationFrame(update)
    }
    update(performance.now())
    window.addEventListener('scroll', schedule, { passive: true })
    window.addEventListener('resize', schedule)
    return () => {
      cancelAnimationFrame(frame)
      window.removeEventListener('scroll', schedule)
      window.removeEventListener('resize', schedule)
    }
  }, [ref, measure, lag])
}
